<template>
  <!-- See comment below re. attr bindings in Vue 2 vs. Vue 3. -->
  <div v-bind="{ class: $attrs.class, style: $attrs.style }">
    <div class="d-flex">
      <div class="label-wrapper flex-grow-1 flex-wrap"
        aria-live="assertive"
      >
        <label :for="id" :class="[
          'form-label',
          { 'text-danger': !!v$.$error }
        ]">
          {{ label }}<span v-if="rules.required" class="text-danger">*</span>
        </label>
        <transition name="slide" mode="out-in">
          <span :id="`error-msg-${id}`"
            :key="v$.$errors[0]?.$message"
            class="error-msg text-danger"
          ><span class="visually-hidden">{{ label }}</span> {{ v$.$errors[0]?.$message }}</span>
        </transition>
      </div>
      <PopOver v-if="helpTip || $slots.helpTip"
        class="me-1"
        placement="top"
        :title="label"
        :show-triggers="['click', 'hover']"
        :hide-triggers="['focus', 'hover']"
      >
        <template #reference>
          <IconButton
            iconClass="bi bi-question-circle"
            :title="`Show help tips for ${ label }`"
          />
        </template>

        <slot name="helpTip">{{ helpTip }}</slot>
      </PopOver>
    </div>

    <LocalScope
      :define="{
        allControlAttrs: {
          ...controlAttrs,
          'aria-describedby': [
            v$.$error ? `error-msg-${id}` : '',
            showDescription ? `description-${id}` : ''
          ].filter(x => x).join(' '),
          'aria-invalid': v$.$error ? 'true' : 'false',
          'aria-required': rules.required ? 'true' : null
        },
        controlClasses: ['form-control', { 'is-invalid': v$.$error }]
      }"
      v-slot="{ allControlAttrs, controlClasses }"
    >
      <slot
        :attrs="allControlAttrs"
        :classes="controlClasses"
      ></slot>
    </LocalScope>

    <div v-if="showDescription"
      :id="`description-${id}`"
    >
      <slot name="description">
        <small class="opacity-75 pt-1 px-1 d-block">{{ description }}</small>
      </slot>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import focusable from '@ssi/vue-common/src/mixins/focusable';
import { defaultSchema } from '@ssi/vue-common/src/directives/focus';
import PopOver from '../PopOver';
import IconButton from '../buttons/Icon';
import { omit } from '../../helpers/util';

const { prop: focusProp } = defaultSchema;

export default {
  setup(props) {
    const formData = computed(() => ({ modelValue: props.modelValue }));

    // A validation error message emitted by the control via an "invalid" event.
    const valueIsInvalidError = ref('');
    // A custom rule to display the validation error emitted by the control.
    const invalidValueRule = {
      $validator: () => !valueIsInvalidError.value,
      $message: () => valueIsInvalidError.value
    };

    return {
      valueIsInvalidError,
      v$: useVuelidate(
        computed(() => ({
          modelValue: {
            invalidValueRule,
            ...props.rules
          }
        })),
        formData
      )
    };
  },

  inheritAttrs: false,

  mixins: [focusable],

  components: { IconButton, PopOver },

  props: {
    description: String,

    helpTip: String,

    label: {
      type: String,
      required: true
    },

    modelValue: { required: true },

    rules: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ['update:modelValue'],

  computed: {
    controlAttrs() {
      return {
        // In Vue 2, $attrs didn't include class and style bindings, which were still applied to
        // the root node despite inheritAttrs. This changed with Vue 3, but we were already relying
        // on this behavior so we have to reproduce it.
        ...omit(this.$attrs, ['class', 'style']),
        modelValue: this.modelValue,
        // Can't use v-focus directive on a slot, so add explicit
        // prop to support it.
        [focusProp]: this[focusProp],
        id: this.id,
        'onUpdate:modelValue': (val) => {
          this.valueIsInvalidError = '';
          this.$emit('update:modelValue', val);
        },
        // Can't use v-focus directive on a slot, so add explicit
        // event handlers to support it.
        onBlur: () => {
          this.hasFocus = false;
          this.v$.$touch();
        },
        onFocus: () => { this.hasFocus = true; },
        onInvalid: (e) => {
          setTimeout(() => {
            this.valueIsInvalidError = e.message;
          }, 1);
        }
      };
    },

    id() { return this.uniqName(this.label.toLowerCase().replace(/\s/g, '-')); },

    showDescription() {
      return Boolean(this.description || this.$slots.description);
    }
  }
};
</script>

<style lang="scss" scoped>
.error-msg {
  font-size: 0.75em;
  font-weight: normal;
  line-height: 1em;
}

.label-wrapper {
  align-items: center;
  display: flex;
  column-gap: 0.5em;
  margin-bottom: 0.5rem;
}

.form-label {
  margin-bottom: 0;
}

$transition-duration: 0.25s;

.form-label {
  transition: color $transition-duration;
}

$translate-distance: 0.8em;

.slide-enter-active, .slide-leave-active {
  transition-duration: $transition-duration;
  transition-property: opacity, transform;
}
.slide-enter-from, .slide-leave-to {
  opacity: 0;
}
.slide-enter-from {
  transform: translateY(-$translate-distance);
}
.slide-leave-to {
  transform: translateY($translate-distance);
}
</style>
